import React, { useState, useEffect } from "react";
import { Button, Badge, Tooltip, Empty, Spin, Progress, Statistic } from "antd";
import {
  SyncOutlined,
  GlobalOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  RightOutlined,
  TeamOutlined,
  MailOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

// css
import "@assets/css/mail-in-a-box.css";

const MailInBoxUsers = ({ mailboxUsers, loading, onSync }) => {
  // State to track expanded/collapsed domains
  const [expandedDomains, setExpandedDomains] = useState({});

  // Initialize all domains as expanded when data changes
  useEffect(() => {
    if (mailboxUsers && mailboxUsers.length > 0) {
      const expandedState = {};
      mailboxUsers.forEach((domain) => {
        expandedState[domain.domain] = true; // Start expanded
      });
      setExpandedDomains(expandedState);
    }
  }, [mailboxUsers]);

  // Toggle domain expansion
  const toggleDomain = (domain) => {
    setExpandedDomains((prev) => ({
      ...prev,
      [domain]: !prev[domain],
    }));
  };

  // Calculate stats
  const activeUsersCount = mailboxUsers.reduce((count, domain) => {
    return (
      count + domain.users.filter((user) => user.status === "active").length
    );
  }, 0);

  const adminUsersCount = mailboxUsers.reduce((count, domain) => {
    return (
      count +
      domain.users.filter(
        (user) => user.privileges && user.privileges.includes("admin")
      ).length
    );
  }, 0);

  const totalUsersCount = mailboxUsers.reduce((count, domain) => {
    return count + domain.users.length;
  }, 0);

  // Get avatar initial from email
  const getAvatarInitials = (email) => {
    if (!email) return "U";
    const parts = email.split("@");
    return parts[0].charAt(0).toUpperCase();
  };

  // Get avatar background color based on email (consistent for same user)
  const getAvatarColor = (email) => {
    if (!email) return { bg: "#e6f7ff", text: "#1890ff" };

    const colors = [
      { bg: "#e6f7ff", text: "#1890ff" }, // Blue
      { bg: "#f6ffed", text: "#52c41a" }, // Green
      { bg: "#fff7e6", text: "#fa8c16" }, // Orange
      { bg: "#fff0f6", text: "#eb2f96" }, // Pink
      { bg: "#f9f0ff", text: "#722ed1" }, // Purple
      { bg: "#fcffe6", text: "#a0d911" }, // Lime
      { bg: "#f4ffb8", text: "#7cb305" }, // Light Green
    ];

    // Simple hash function to get consistent color for same email
    const hash = email
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="mail-box-container">
      {/* Sync button */}
      <Button
        onClick={onSync}
        loading={loading}
        icon={<SyncOutlined spin={loading} />}
        className="mail-box-sync-button"
      >
        Sync with Mail-in-a-Box
      </Button>

      {/* Stats summary cards */}
      <div className="mail-box-stats">
        <div className="mail-box-stat-card">
          <div className="mail-box-stat-icon">
            <GlobalOutlined />
          </div>
          <div className="mail-box-stat-content">
            <Statistic
              value={mailboxUsers.length}
              suffix="Domains"
              valueStyle={{ fontSize: "22px" }}
            />
            <div className="mail-box-stat-progress">
              <Progress
                percent={100}
                showInfo={false}
                strokeColor="#1890ff"
                trailColor="#e6f7ff"
                size="small"
              />
            </div>
          </div>
        </div>

        <div className="mail-box-stat-card">
          <div
            className="mail-box-stat-icon"
            style={{ backgroundColor: "#f6ffed", color: "#52c41a" }}
          >
            <TeamOutlined />
          </div>
          <div className="mail-box-stat-content">
            <Statistic
              value={activeUsersCount}
              suffix="Active Users"
              valueStyle={{ fontSize: "22px" }}
            />
            <div className="mail-box-stat-progress">
              <Progress
                percent={
                  totalUsersCount
                    ? Math.round((activeUsersCount / totalUsersCount) * 100)
                    : 0
                }
                showInfo={false}
                strokeColor="#52c41a"
                trailColor="#f6ffed"
                size="small"
              />
            </div>
          </div>
        </div>

        <div className="mail-box-stat-card">
          <div
            className="mail-box-stat-icon"
            style={{ backgroundColor: "#e6f7ff", color: "#1890ff" }}
          >
            <LockOutlined />
          </div>
          <div className="mail-box-stat-content">
            <Statistic
              value={adminUsersCount}
              suffix="Admin Users"
              valueStyle={{ fontSize: "22px" }}
            />
            <div className="mail-box-stat-progress">
              <Progress
                percent={
                  totalUsersCount
                    ? Math.round((adminUsersCount / totalUsersCount) * 100)
                    : 0
                }
                showInfo={false}
                strokeColor="#1890ff"
                trailColor="#e6f7ff"
                size="small"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable domains content */}
      <Spin spinning={loading}>
        <div className="mail-box-domains-wrapper">
          {mailboxUsers.length > 0 ? (
            mailboxUsers.map((domainData) => (
              <div key={domainData.domain} className="mail-box-domain-card">
                <div
                  className="mail-box-domain-header"
                  onClick={() => toggleDomain(domainData.domain)}
                >
                  <div className="mail-box-domain-title-section">
                    {expandedDomains[domainData.domain] ? (
                      <DownOutlined className="mail-box-expand-icon" />
                    ) : (
                      <RightOutlined className="mail-box-expand-icon" />
                    )}
                    <GlobalOutlined className="mail-box-domain-icon" />
                    <h3 className="mail-box-domain-title">
                      {domainData.domain}
                    </h3>
                  </div>

                  <div className="mail-box-domain-stats">
                    <Tooltip title="Total Users">
                      <Badge
                        count={domainData.users.length}
                        style={{ backgroundColor: "#fff", color: "#1890ff" }}
                        className="mail-box-domain-badge"
                      />
                    </Tooltip>

                    <Tooltip title="Active Users">
                      <Badge
                        count={
                          domainData.users.filter((u) => u.status === "active")
                            .length
                        }
                        style={{ backgroundColor: "#f6ffed", color: "#52c41a" }}
                        className="mail-box-domain-badge"
                      />
                    </Tooltip>

                    <Tooltip title="Admin Users">
                      <Badge
                        count={
                          domainData.users.filter(
                            (u) =>
                              u.privileges && u.privileges.includes("admin")
                          ).length
                        }
                        style={{ backgroundColor: "#e6f7ff", color: "#1890ff" }}
                        className="mail-box-domain-badge"
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* User list - only show when expanded */}
                <div
                  className={`mail-box-users-container ${
                    expandedDomains[domainData.domain] ? "expanded" : ""
                  }`}
                >
                  {domainData.users.length > 0 ? (
                    domainData.users.map((user, index) => {
                      const avatarColor = getAvatarColor(user.email);
                      return (
                        <div
                          key={`${user.email}-${index}`}
                          className="mail-box-user-item"
                        >
                          <div
                            className="mail-box-user-avatar"
                            style={{
                              backgroundColor: avatarColor.bg,
                              color: avatarColor.text,
                            }}
                          >
                            {getAvatarInitials(user.email)}
                          </div>

                          <div className="mail-box-user-info">
                            <div className="mail-box-user-email">
                              <MailOutlined className="mail-box-user-icon" />
                              {user.email}
                            </div>

                            {user.mailbox && (
                              <div className="mail-box-user-mailbox">
                                <HomeOutlined className="mail-box-user-icon" />
                                {user.mailbox}
                              </div>
                            )}
                          </div>

                          <div className="mail-box-user-attributes">
                            {user.privileges &&
                              user.privileges.includes("admin") && (
                                <Tooltip title="Administrator">
                                  <span className="mail-box-badge mail-box-admin-badge">
                                    <LockOutlined className="mail-box-badge-icon" />
                                    Admin
                                  </span>
                                </Tooltip>
                              )}

                            <Tooltip title={`Status: ${user.status}`}>
                              <span
                                className={`mail-box-badge ${
                                  user.status === "active"
                                    ? "mail-box-status-active"
                                    : "mail-box-status-inactive"
                                }`}
                              >
                                {user.status === "active" ? (
                                  <>
                                    <CheckCircleOutlined className="mail-box-badge-icon" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <ClockCircleOutlined className="mail-box-badge-icon" />
                                    Inactive
                                  </>
                                )}
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="mail-box-empty">
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No users found for this domain"
                      />
                    </div>
                  )}
                </div>

                {/* Summary for collapsed state */}
                {!expandedDomains[domainData.domain] && (
                  <div className="mail-box-domain-summary">
                    <Tooltip title="Total Users">
                      <div className="mail-box-summary-item">
                        <TeamOutlined />
                        <span>{domainData.users.length} users</span>
                      </div>
                    </Tooltip>

                    <Tooltip title="Active Users">
                      <div className="mail-box-summary-item">
                        <CheckCircleOutlined />
                        <span>
                          {
                            domainData.users.filter(
                              (u) => u.status === "active"
                            ).length
                          }{" "}
                          active
                        </span>
                      </div>
                    </Tooltip>

                    <Tooltip title="Click to expand">
                      <InfoCircleOutlined className="mail-box-summary-hint" />
                    </Tooltip>
                  </div>
                )}
              </div>
            ))
          ) : (
            <Empty
              description="No domains found"
              style={{ margin: "40px 0" }}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default MailInBoxUsers;
